/**
 * OCC lens registry (S4.3) — the route-derived tab set (`/auth/occ/:lens`).
 *
 * v1 cut (ux-lens finding 5, converging toward the charter 5-owned split
 * pending the G4 re-open):
 * - OWNED tabs: 總覽 (landing) · 訂單明細 · 供應商確認 (read-only, drill to
 *   PO detail — OCC hosts NO write drawer) · 訂單異常.
 * - SO/PO 對照 is an Order Lines GROUPING TOGGLE, not a tab; 待出量 is a
 *   stat-card drill / saved filter of Order Lines, not a tab.
 * - 裝櫃批次 was registered tab-hidden until W6 (hide-don't-disable: the deep
 *   link always rendered); S6.1 unhid the tab once batches went live on the
 *   rebuilt shipping surface.
 * - REFERENCED lenses (開票狀態 · 收貨狀態): visibly read-only, no action
 *   column, every row a SourceStampTag.
 */

export const OCC_LENS_KEYS = [
  'overview',
  'lines',
  'vendor-confirm',
  'exceptions',
  'batches',
  'invoice-status',
  'receipt-status',
] as const;

export type OccLensKey = (typeof OCC_LENS_KEYS)[number];

export interface OccLensDef {
  key: OccLensKey;
  /** Tab label key in the `occ` namespace. */
  i18nKey: string;
  kind: 'owned' | 'referenced';
  /** Registered but not shown as a tab (deep link still renders). */
  tabHidden?: boolean;
}

export const OCC_LENSES: readonly OccLensDef[] = [
  { key: 'overview', i18nKey: 'occ.lenses.overview', kind: 'owned' },
  { key: 'lines', i18nKey: 'occ.lenses.lines', kind: 'owned' },
  { key: 'vendor-confirm', i18nKey: 'occ.lenses.vendorConfirm', kind: 'owned' },
  { key: 'exceptions', i18nKey: 'occ.lenses.exceptions', kind: 'owned' },
  // Tab unhidden at S6.1 — 裝櫃批次 exist on the rebuilt shipping surface.
  { key: 'batches', i18nKey: 'occ.lenses.batches', kind: 'owned' },
  { key: 'invoice-status', i18nKey: 'occ.lenses.invoiceStatus', kind: 'referenced' },
  { key: 'receipt-status', i18nKey: 'occ.lenses.receiptStatus', kind: 'referenced' },
];

/** Landing lens = 訂單進度總覽 Progress Overview (charter; plan conflict #5). */
export const OCC_DEFAULT_LENS: OccLensKey = 'overview';

export function isOccLensKey(value: string | undefined): value is OccLensKey {
  return Boolean(value) && (OCC_LENS_KEYS as readonly string[]).includes(value as string);
}
